import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  Box,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import CallIcon from "@material-ui/icons/Call";
import PhoneIcon from "@material-ui/icons/Phone";
import SettingsIcon from "@material-ui/icons/Settings";
import PauseIcon from "@material-ui/icons/Pause";
import SwapCallsIcon from "@material-ui/icons/SwapCalls";
import CallEndIcon from "@material-ui/icons/CallEnd";
import SaveIcon from "@material-ui/icons/Save";
import MinimizeIcon from "@material-ui/icons/Remove";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import { toast } from "react-toastify";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { connectToAsteriskSIP, makeCall, hangupCall, holdCall, unholdCall } from "./connectSIP";
import { playDTMFTone, playRingtone, stopRingtone } from "./audioUtils";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: 320,
      maxWidth: 400,
      position: "relative",
    },
  },
  floatingDialog: {
    "& .MuiDialog-paper": {
      position: "fixed",
      bottom: 20,
      right: 20,
      margin: 0,
      minWidth: 320,
      maxWidth: 400,
      maxHeight: "90vh",
      cursor: "move",
    },
  },
  minimizedDialog: {
    "& .MuiBackdrop-root": {
      display: "none !important", // Remover backdrop quando minimizado
      pointerEvents: "none !important", // Garantir que não bloqueie cliques
      visibility: "hidden !important",
      opacity: "0 !important",
    },
    "& .MuiDialog-container": {
      pointerEvents: "none !important", // Container não bloqueia cliques
      position: "static !important",
    },
    "& .MuiDialog-root": {
      pointerEvents: "none !important", // Root não bloqueia cliques
    },
    "& .MuiDialog-paper": {
      position: "fixed",
      bottom: 20,
      right: 20,
      margin: 0,
      minWidth: 60,
      maxWidth: 60,
      width: 60,
      height: 60,
      borderRadius: "50%",
      padding: 0,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.primary.main,
      boxShadow: theme.shadows[8],
      zIndex: 1300, // Garantir que fique acima de outros elementos
      pointerEvents: "auto !important", // Apenas o círculo é clicável
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
        transform: "scale(1.1)",
        transition: "all 0.2s ease-in-out",
      },
    },
  },
  minimizedContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    fontSize: "2rem",
    cursor: "pointer",
  },
  dragHandle: {
    cursor: "move",
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    userSelect: "none",
  },
  dialpad: {
    padding: theme.spacing(2),
  },
  dialButton: {
    minWidth: 60,
    minHeight: 60,
    fontSize: "1.2rem",
    fontWeight: "bold",
  },
  numberDisplay: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    textAlign: "center",
    fontSize: "1.5rem",
    fontWeight: "bold",
    minHeight: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  actionButton: {
    margin: theme.spacing(1),
  },
  settingsSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  callButton: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  endCallButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

const PhoneDialer = ({ open, onClose }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { getAll: getAllSettings } = useCompanySettings();
  
  const [number, setNumber] = useState("");
  const [extension, setExtension] = useState("");
  const [password, setPassword] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [asteriskUrl, setAsteriskUrl] = useState("");
  const [sipUA, setSipUA] = useState(null); // SIP.js UserAgent wrapper
  const [currentCall, setCurrentCall] = useState(null); // Chamada atual
  const [callStatus, setCallStatus] = useState("idle"); // idle, calling, connected, onHold, ringing
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState("");
  const [useInsecureConnection, setUseInsecureConnection] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastMessage, setLastMessage] = useState("");
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false); // Rastrear se houve movimento durante o arrasto
  const dialogRef = useRef(null);
  const remoteAudioRef = useRef(null); // Referência para elemento de áudio remoto
  const localAudioRef = useRef(null); // Referência para elemento de áudio local (opcional, para monitoramento)

  // Função para configurar streams de áudio quando chamada é aceita
  const setupAudioStreams = (session) => {
    try {
      // Função auxiliar para obter o stream remoto
      const getRemoteStream = () => {
        // sip.js 0.7.8 usa getRemoteStreams() (plural) que retorna um array
        if (session.getRemoteStreams) {
          const streams = session.getRemoteStreams();
          if (streams && streams.length > 0) {
            // Retornar o primeiro stream que tenha tracks de áudio
            for (const stream of streams) {
              if (stream && stream.getTracks) {
                const audioTracks = stream.getTracks().filter(t => t.kind === 'audio');
                if (audioTracks.length > 0) {
                  return stream;
                }
              }
            }
          }
        }
        
        // Tentar através do mediaHandler
        if (session.mediaHandler) {
          // Verificar se mediaHandler tem getRemoteStream ou getRemoteStreams
          if (session.mediaHandler.getRemoteStream) {
            const stream = session.mediaHandler.getRemoteStream();
            if (stream && stream.getTracks && stream.getTracks().length > 0) {
              return stream;
            }
          }
          
          if (session.mediaHandler.getRemoteStreams) {
            const streams = session.mediaHandler.getRemoteStreams();
            if (streams && streams.length > 0) {
              for (const stream of streams) {
                if (stream && stream.getTracks) {
                  const audioTracks = stream.getTracks().filter(t => t.kind === 'audio');
                  if (audioTracks.length > 0) {
                    return stream;
                  }
                }
              }
            }
          }
          
          // Tentar acessar peerConnection através do mediaHandler
          if (session.mediaHandler.peerConnection) {
            const pc = session.mediaHandler.peerConnection;
            try {
              const receivers = pc.getReceivers();
              if (receivers && receivers.length > 0) {
                const stream = new MediaStream();
                receivers.forEach(receiver => {
                  if (receiver.track && receiver.track.kind === 'audio') {
                    stream.addTrack(receiver.track);
                  }
                });
                if (stream.getTracks().length > 0) {
                  return stream;
                }
              }
            } catch (err) {
              // Silencioso - tentar próximo método
            }
          }
        }
        
        // Tentar acessar peerConnection diretamente da sessão
        if (session.peerConnection) {
          try {
            const receivers = session.peerConnection.getReceivers();
            if (receivers && receivers.length > 0) {
              const stream = new MediaStream();
              receivers.forEach(receiver => {
                if (receiver.track && receiver.track.kind === 'audio') {
                  stream.addTrack(receiver.track);
                }
              });
              if (stream.getTracks().length > 0) {
                return stream;
              }
            }
          } catch (err) {
              // Silencioso - tentar próximo método
          }
        }
        
        // Tentar propriedades diretas (menos comum)
        if (session.remoteStream) {
          return session.remoteStream;
        }
        
        return null;
      };
      
      // Função auxiliar para conectar stream ao elemento de áudio
      const connectStreamToAudio = (stream) => {
        if (!stream || stream.getTracks().length === 0) {
          return false;
        }
        
        // Criar ou usar elemento de áudio existente
        if (!remoteAudioRef.current) {
          const audioElement = document.createElement('audio');
          audioElement.autoplay = true;
          audioElement.playsInline = true;
          audioElement.style.display = 'none';
          document.body.appendChild(audioElement);
          remoteAudioRef.current = audioElement;
        }
        
        // Conectar stream ao elemento de áudio
        remoteAudioRef.current.srcObject = stream;
        
        // Tentar reproduzir
        const playPromise = remoteAudioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Tentar novamente após interação do usuário (requisito do navegador)
            const playAudio = () => {
              if (remoteAudioRef.current && remoteAudioRef.current.srcObject) {
                remoteAudioRef.current.play().catch(() => {});
              }
            };
            document.addEventListener('click', playAudio, { once: true });
            document.addEventListener('touchstart', playAudio, { once: true });
          });
        }
        
        return true;
      };
      
      // Função para verificar se peerConnection está pronto
      const isPeerConnectionReady = () => {
        let pc = null;
        if (session.peerConnection) {
          pc = session.peerConnection;
        } else if (session.mediaHandler && session.mediaHandler.peerConnection) {
          pc = session.mediaHandler.peerConnection;
        }
        
        if (!pc) return false;
        
        const connectionState = pc.connectionState;
        const iceState = pc.iceConnectionState;
        
        // Retornar true se estiver connected ou connecting (pode funcionar)
        return connectionState === 'connected' || connectionState === 'connecting' || 
               iceState === 'connected' || iceState === 'completed';
      };
      
      // Tentar obter o stream imediatamente
      let remoteStream = getRemoteStream();
      
      if (remoteStream && connectStreamToAudio(remoteStream)) {
        return; // Sucesso!
      }
      
      // Se não encontrou, tentar aguardar um pouco (stream pode não estar pronto ainda)
      let attempts = 0;
      const maxAttempts = 20; // Tentar por até 10 segundos (20 * 500ms)
      
      const tryAgain = () => {
        attempts++;
        
        // Verificar se peerConnection está pronto antes de tentar obter stream
        if (!isPeerConnectionReady() && attempts < 5) {
          // Aguardar mais um pouco se ainda não estiver pronto
          setTimeout(tryAgain, 500);
          return;
        }
        
        remoteStream = getRemoteStream();
        
        if (remoteStream && connectStreamToAudio(remoteStream)) {
          return; // Sucesso!
        }
        
        if (attempts < maxAttempts) {
          setTimeout(tryAgain, 500);
        }
      };
      
      setTimeout(tryAgain, 500);
      
      // Configurar listeners para eventos do peerConnection
      const setupPeerConnectionListeners = () => {
        let peerConnection = null;
        
        // Tentar obter peerConnection de diferentes lugares
        if (session.peerConnection) {
          peerConnection = session.peerConnection;
        } else if (session.mediaHandler && session.mediaHandler.peerConnection) {
          peerConnection = session.mediaHandler.peerConnection;
        }
        
        if (peerConnection) {
          // Listener para quando um track é adicionado
          const onTrack = (event) => {
            if (event.streams && event.streams.length > 0) {
              // Usar o primeiro stream que tenha tracks de áudio
              for (const stream of event.streams) {
                if (stream && stream.getTracks) {
                  const audioTracks = stream.getTracks().filter(t => t.kind === 'audio');
                  if (audioTracks.length > 0 && !remoteAudioRef.current?.srcObject) {
                    connectStreamToAudio(stream);
                    return;
                  }
                }
              }
            } else if (event.track && event.track.kind === 'audio') {
              // Criar stream a partir do track
              const stream = new MediaStream([event.track]);
              if (!remoteAudioRef.current?.srcObject) {
                connectStreamToAudio(stream);
              }
            }
          };
          
          // Adicionar listener
          peerConnection.addEventListener('track', onTrack);
          
          // Também verificar se já existem receivers (pode ter chegado antes do listener)
          try {
            const receivers = peerConnection.getReceivers();
            if (receivers && receivers.length > 0) {
              const audioReceivers = receivers.filter(r => r.track && r.track.kind === 'audio');
              if (audioReceivers.length > 0 && !remoteAudioRef.current?.srcObject) {
                const stream = new MediaStream();
                audioReceivers.forEach(receiver => {
                  if (receiver.track) {
                    stream.addTrack(receiver.track);
                  }
                });
                if (stream.getTracks().length > 0) {
                  connectStreamToAudio(stream);
                }
              }
            }
          } catch (err) {
            // Silencioso
          }
          
          // Verificar quando a conexão estiver estabelecida
          peerConnection.addEventListener('connectionstatechange', () => {
            if (peerConnection.connectionState === 'connected' && !remoteAudioRef.current?.srcObject) {
              // Tentar obter stream novamente quando conexão estiver estabelecida
              setTimeout(() => {
                const stream = getRemoteStream();
                if (stream) {
                  connectStreamToAudio(stream);
                }
              }, 100);
            }
          });
        }
      };
      
      // Configurar listeners
      setupPeerConnectionListeners();
      
    } catch (error) {
      // Silencioso - erro ao configurar streams
    }
  };

  // Função para limpar streams de áudio
  const cleanupAudioStreams = () => {
    try {
      if (remoteAudioRef.current) {
        if (remoteAudioRef.current.srcObject) {
          remoteAudioRef.current.srcObject.getTracks().forEach(track => track.stop());
          remoteAudioRef.current.srcObject = null;
        }
        remoteAudioRef.current.pause();
        if (remoteAudioRef.current.parentNode) {
          remoteAudioRef.current.parentNode.removeChild(remoteAudioRef.current);
        }
        remoteAudioRef.current = null;
      }
      
      if (localAudioRef.current) {
        if (localAudioRef.current.srcObject) {
          localAudioRef.current.srcObject.getTracks().forEach(track => track.stop());
          localAudioRef.current.srcObject = null;
        }
        localAudioRef.current.pause();
        if (localAudioRef.current.parentNode) {
          localAudioRef.current.parentNode.removeChild(localAudioRef.current);
        }
        localAudioRef.current = null;
      }
    } catch (error) {
      console.error('Erro ao limpar streams de áudio:', error);
    }
  };

  // Carregar configurações salvas
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Carregar do localStorage
        const savedExtension = localStorage.getItem("voip_extension");
        const savedPassword = localStorage.getItem("voip_password");
        const savedUseInsecure = localStorage.getItem("voip_use_insecure");
        
        if (savedExtension) setExtension(savedExtension);
        if (savedPassword) setPassword(savedPassword);
        if (savedUseInsecure === "true") setUseInsecureConnection(true);

        // Carregar URL do Asterisk das configurações da empresa
        if (user?.companyId) {
          try {
            const settings = await getAllSettings(user.companyId);
            if (settings && settings.asteriskWebSocketUrl) {
              setAsteriskUrl(settings.asteriskWebSocketUrl);
            }
          } catch (error) {
            console.error("Erro ao carregar configurações do Asterisk:", error);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar configurações:", error);
      }
    };

    if (open) {
      loadSettings();
    }
  }, [open, user, getAllSettings]);

  // Conectar ao Asterisk usando JsSIP
  const connectToAsterisk = () => {
    if (!asteriskUrl) {
      toast.error("Configure o endereço do Asterisk WebSocket nas configurações");
      return;
    }

    if (!extension || !password) {
      toast.error("Configure o ramal e senha");
      setShowSettings(true);
      return;
    }

    // Fechar conexão anterior se existir
    if (sipUA) {
      sipUA.stop();
      setSipUA(null);
    }
    
    // Fechar chamada atual se existir
    if (currentCall) {
      hangupCall(currentCall);
      setCurrentCall(null);
    }

    try {
      // Normalizar a URL
      let url = asteriskUrl.trim();
      url = url.replace(/^(ws|wss|http|https):\/\//, '');
      url = url.replace(/^\/+/, '');
      const urlParts = url.split('/');
      const hostPort = urlParts[0];
      url = `${hostPort}/ws`;
      
      if (useInsecureConnection) {
        url = `ws://${url}`;
      } else {
        url = `wss://${url}`;
      }

      console.log("🔌 Conectando ao Asterisk via JsSIP:", url);
      setConnectionError("");

      // Usar JsSIP para conectar
      const ua = connectToAsteriskSIP(
        { asteriskUrl: url, extension, password, useInsecureConnection },
        {
          onConnected: () => {
            setIsConnected(true);
            toast.success("Conectado ao Asterisk");
            setLastMessage("Conectado...");
          },
          onDisconnected: () => {
            setIsConnected(false);
            setIsAuthenticated(false);
            setSipUA(null);
            toast.info("Desconectado do Asterisk");
            setLastMessage("Desconectado");
          },
          onRegistered: () => {
            setIsAuthenticated(true);
            toast.success("Registrado no Asterisk");
            setLastMessage("✓ Registrado e pronto");
          },
          onRegistrationFailed: (e) => {
            setIsAuthenticated(false);
            toast.error("Falha no registro. Verifique ramal e senha.");
            setLastMessage(`✗ Falha no registro: ${e.cause || "Erro desconhecido"}`);
            setConnectionError(`Falha no registro: ${e.cause || "Erro desconhecido"}`);
          },
          onError: (error) => {
            console.error("❌ Erro JsSIP:", error);
            toast.error("Erro ao conectar ao Asterisk");
            setConnectionError(error.message || "Erro desconhecido");
            setIsConnected(false);
          },
          onCallProgress: (session) => {
            setCurrentCall(session);
            setCallStatus("calling");
            setLastMessage("Chamada em progresso...");
          },
          onCallAccepted: (session) => {
            stopRingtone(); // Parar ringtone quando chamada é aceita
            setCurrentCall(session);
            setCallStatus("connected");
            toast.success("Chamada conectada");
            setLastMessage("Chamada conectada");
            // Configurar áudio remoto
            setupAudioStreams(session);
          },
          onCallEnded: () => {
            stopRingtone(); // Parar ringtone quando chamada termina
            setCurrentCall(null);
            setCallStatus("idle");
            setNumber("");
            toast.info("Chamada encerrada");
            setLastMessage("Chamada encerrada");
          },
          onCallFailed: (e) => {
            stopRingtone(); // Parar ringtone se falhar
            setCurrentCall(null);
            setCallStatus("idle");
            toast.error(`Chamada falhou: ${e.cause || "Erro desconhecido"}`);
            setLastMessage(`✗ Chamada falhou: ${e.cause || "Erro desconhecido"}`);
          },
          onIncomingCall: (session) => {
            // Chamada recebida
            console.log('📞 Chamada recebida!', session);
            
            // Extrair informações do chamador
            let callerName = "Desconhecido";
            let callerNumber = "Desconhecido";
            
            try {
              // No sip.js 0.7.8, tentar acessar o request
              if (session.request) {
                const fromHeader = session.request.getHeader('from');
                console.log('📞 From header:', fromHeader);
                if (fromHeader) {
                  // Formato: "Nome" <sip:numero@dominio> ou sip:numero@dominio
                  const match = fromHeader.match(/^"?([^"<]+)"?\s*<sip:([^@]+)@/);
                  if (match) {
                    callerName = match[1].trim() || match[2].trim();
                    callerNumber = match[2].trim();
                  } else {
                    // Tentar extrair apenas o número
                    const numberMatch = fromHeader.match(/sip:([^@]+)@/);
                    if (numberMatch) {
                      callerNumber = numberMatch[1];
                      callerName = callerNumber;
                    }
                  }
                }
              } else if (session.remote_identity) {
                // Tentar usar remote_identity se disponível
                const remote = session.remote_identity;
                callerName = remote.display_name || remote.user || "Desconhecido";
                callerNumber = remote.user || "Desconhecido";
              } else if (session.request && session.request.from) {
                // Tentar acessar from diretamente
                const from = session.request.from;
                callerName = from.display_name || from.user || "Desconhecido";
                callerNumber = from.user || "Desconhecido";
              }
            } catch (error) {
              console.error("Erro ao extrair informações do chamador:", error);
            }
            
            setCurrentCall(session);
            setCallStatus("ringing");
            playRingtone(); // Tocar ringtone para chamada recebida
            
            // Criar toast customizado com botões de ação
            const toastId = `incoming-call-${Date.now()}`;
            
            // Armazenar referência da sessão para usar nos callbacks
            const incomingSession = session;
            
            const IncomingCallContent = (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '250px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  📞 Chamada recebida
                </div>
                <div style={{ fontSize: '14px' }}>
                  <strong>{callerName}</strong>
                  {callerNumber !== callerName && ` (${callerNumber})`}
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                      toast.dismiss(toastId);
                      stopRingtone();
                      try {
                        incomingSession.accept();
                        setCurrentCall(incomingSession);
                        setCallStatus("connected");
                        toast.success("Chamada atendida");
                        setLastMessage("Chamada atendida");
                        // Configurar áudio remoto
                        setupAudioStreams(incomingSession);
                        setTimeout(() => {
                          stopRingtone();
                        }, 100);
                      } catch (error) {
                        console.error("Erro ao aceitar chamada:", error);
                        toast.error("Erro ao aceitar chamada");
                        stopRingtone();
                      }
                    }}
                    style={{ flex: 1 }}
                  >
                    Atender
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => {
                      toast.dismiss(toastId);
                      stopRingtone();
                      hangupCall(incomingSession);
                      setCurrentCall(null);
                      setCallStatus("idle");
                      toast.info("Chamada rejeitada");
                      setLastMessage("Chamada rejeitada");
                    }}
                    style={{ flex: 1 }}
                  >
                    Rejeitar
                  </Button>
                </div>
              </div>
            );
            
            toast(IncomingCallContent, {
              toastId: toastId,
              autoClose: false,
              closeOnClick: false,
              draggable: true,
              position: "top-right",
            });
            
            setLastMessage(`📞 Chamada de ${callerName}`);
          },
        }
      );

      if (ua) {
        setSipUA(ua);
      }
    } catch (error) {
      console.error("❌ Erro ao conectar ao Asterisk:", error);
      toast.error(`Erro ao conectar: ${error.message || "Erro desconhecido"}`);
      setIsConnected(false);
      setConnectionError(error.message || "Erro desconhecido");
    }
  };

  const handleDial = (digit) => {
    setNumber((prev) => prev + digit);
    // Tocar tom DTMF
    playDTMFTone(digit);
  };

  const handleBackspace = () => {
    setNumber((prev) => prev.slice(0, -1));
  };

  const handleCall = useCallback(() => {
    if (!number) {
      toast.error("Digite um número para discar");
      return;
    }

    if (!isConnected || !sipUA) {
      toast.error("Conecte-se ao Asterisk primeiro");
      connectToAsterisk();
      return;
    }

    if (!isAuthenticated || !sipUA || !sipUA.isRegistered()) {
      toast.warning("Aguardando registro no Asterisk...");
      return;
    }

    if (currentCall) {
      toast.warning("Já existe uma chamada em andamento");
      return;
    }

    try {
      // Extrair domínio da URL
      let url = asteriskUrl.trim();
      url = url.replace(/^(ws|wss|http|https):\/\//, '');
      url = url.replace(/^\/+/, '');
      const urlParts = url.split('/');
      const hostPort = urlParts[0];
      const domain = hostPort.split(':')[0];

      console.log("📞 Discando para:", number, "via domínio:", domain);
      setLastMessage(`Discando para ${number}...`);
      
      const session = makeCall(sipUA, number, extension, domain);
      setCurrentCall(session);
      setCallStatus("calling");
      playRingtone(); // Tocar ringtone ao chamar
      toast.info(`Discando para ${number}...`);
      
      // Configurar event handlers para a sessão de chamada
      // IMPORTANTE: Parar ringtone quando a chamada for aceita
      session.on('progress', () => {
        setCallStatus("calling");
        // Tentar configurar áudio também no progress (pode estar disponível mais cedo)
        setTimeout(() => {
          setupAudioStreams(session);
        }, 500);
      });

      session.on('accepted', () => {
        stopRingtone(); // Parar ringtone quando chamada é aceita
        setCallStatus("connected");
        toast.success("Chamada conectada");
        setLastMessage("Chamada conectada");
        // Configurar áudio remoto com um pequeno delay para dar tempo ao WebRTC
        setTimeout(() => {
          setupAudioStreams(session);
        }, 300);
      });
      
      // Também tentar configurar áudio quando progress for disparado (mídia sendo negociada)
      session.on('progress', () => {
        // Tentar configurar áudio também no progress (pode estar disponível mais cedo)
        setTimeout(() => {
          setupAudioStreams(session);
        }, 500);
      });

      session.on('bye', () => {
        stopRingtone(); // Parar ringtone quando chamada termina
        setCurrentCall(null);
        setCallStatus("idle");
        setNumber("");
        toast.info("Chamada encerrada");
        setLastMessage("Chamada encerrada");
      });

      session.on('cancel', () => {
        stopRingtone(); // Parar ringtone quando chamada é cancelada
        setCurrentCall(null);
        setCallStatus("idle");
        setNumber("");
        toast.info("Chamada cancelada");
        setLastMessage("Chamada cancelada");
      });
      
      session.on('failed', (error) => {
        stopRingtone(); // Parar ringtone quando chamada falha
        setCurrentCall(null);
        setCallStatus("idle");
        toast.error(`Chamada falhou: ${error.message || "Erro desconhecido"}`);
        setLastMessage(`✗ Chamada falhou: ${error.message || "Erro desconhecido"}`);
      });
    } catch (error) {
      console.error("❌ Erro ao discar:", error);
      toast.error(`Erro ao discar: ${error.message || "Erro desconhecido"}`);
      setLastMessage(`Erro ao discar: ${error.message || "Erro desconhecido"}`);
    }
  }, [number, isConnected, sipUA, isAuthenticated, currentCall, asteriskUrl, extension, connectToAsterisk]);

  const handleAnswerCall = () => {
    if (currentCall && callStatus === "ringing") {
      // Parar ringtone antes de aceitar
      stopRingtone();
      // No sip.js 0.7.8, accept() não retorna Promise, é síncrono
      try {
        currentCall.accept();
        setCallStatus("connected");
        toast.success("Chamada atendida");
        setLastMessage("Chamada atendida");
        // Configurar áudio remoto
        setupAudioStreams(currentCall);
        
        // Garantir que o ringtone está parado após aceitar
        setTimeout(() => {
          stopRingtone();
        }, 100);
      } catch (error) {
        console.error("Erro ao aceitar chamada:", error);
        toast.error("Erro ao aceitar chamada");
        stopRingtone(); // Parar ringtone mesmo em caso de erro
      }
    }
  };

  const handleRejectCall = () => {
    stopRingtone();
    if (currentCall) {
      hangupCall(currentCall);
      setCurrentCall(null);
    }
    setCallStatus("idle");
    toast.info("Chamada rejeitada");
    setLastMessage("Chamada rejeitada");
  };

  const handleEndCall = () => {
    stopRingtone(); // Parar ringtone
    // Limpar streams de áudio
    cleanupAudioStreams();
    if (currentCall) {
      hangupCall(currentCall);
      setCurrentCall(null);
    }
    setCallStatus("idle");
    setNumber("");
  };

  const handleHold = () => {
    if (currentCall && callStatus === "connected") {
      try {
        holdCall(currentCall);
        setCallStatus("onHold");
        toast.info("Chamada em espera");
        setLastMessage("Chamada em espera");
      } catch (error) {
        console.error("Erro ao colocar em espera:", error);
        toast.error("Erro ao colocar chamada em espera");
      }
    }
  };

  const handleUnhold = () => {
    if (currentCall && callStatus === "onHold") {
      try {
        unholdCall(currentCall);
        setCallStatus("connected");
        toast.info("Chamada retomada");
        setLastMessage("Chamada retomada");
      } catch (error) {
        console.error("Erro ao retomar chamada:", error);
        toast.error("Erro ao retomar chamada");
      }
    }
  };

  const handleTransfer = () => {
    if (currentCall && callStatus === "connected") {
      const transferNumber = prompt("Digite o número para transferir:");
      if (transferNumber) {
        // Transferir usando JsSIP
        try {
          currentCall.refer(`sip:${transferNumber}@${asteriskUrl.split('/')[0].split(':')[0]}`);
          toast.info(`Transferindo para ${transferNumber}`);
        } catch (error) {
          console.error("Erro ao transferir:", error);
          toast.error("Erro ao transferir chamada");
        }
      }
    }
  };

  const handleSaveCredentials = () => {
    localStorage.setItem("voip_extension", extension);
    localStorage.setItem("voip_password", password);
    localStorage.setItem("voip_use_insecure", useInsecureConnection ? "true" : "false");
    toast.success("Credenciais salvas");
    setShowSettings(false);
  };

  const handleClose = () => {
    // Limpar streams de áudio
    cleanupAudioStreams();
    if (currentCall) {
      hangupCall(currentCall);
      setCurrentCall(null);
    }
    if (sipUA) {
      sipUA.stop();
      setSipUA(null);
    }
    setCallStatus("idle");
    setNumber("");
    setIsConnected(false);
    setIsAuthenticated(false);
    onClose();
  };

  // Handlers para drag and drop
  const handleMouseDown = (e) => {
    // Permitir arrastar tanto pela barra de título quanto pelo círculo minimizado
    if (e.target.closest('.MuiDialogTitle-root') || 
        e.target.closest('.drag-handle') || 
        e.target.closest('.minimizedContent') ||
        isMinimized) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setHasMoved(false); // Resetar flag de movimento
      const rect = dialogRef.current?.getBoundingClientRect();
      if (rect) {
        // Sempre calcular offset baseado em top/left para simplificar
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      // Verificar se houve movimento significativo (mais de 3px)
      if (!hasMoved && (Math.abs(e.movementX) > 3 || Math.abs(e.movementY) > 3)) {
        setHasMoved(true); // Marcar que houve movimento
      }
      
      // Calcular nova posição baseada em top/left
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Limitar dentro da tela
      const maxX = window.innerWidth - 60;
      const maxY = window.innerHeight - 60;
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };

  const handleMouseUp = (e) => {
    const moved = hasMoved;
    setIsDragging(false);
    
    // Se estava minimizado e não houve movimento significativo, restaurar (foi um clique, não arrasto)
    if (isMinimized && !moved) {
      // Pequeno delay para garantir que não foi um arrasto muito rápido
      setTimeout(() => {
        setIsMinimized(false);
      }, 150);
    }
    
    setHasMoved(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Parar ringtone e limpar streams quando componente desmonta
  useEffect(() => {
    return () => {
      stopRingtone();
      cleanupAudioStreams();
    };
  }, []);

  // Suporte a teclado para digitar números e Enter para discar
  useEffect(() => {
    if (!open || isMinimized) return;

    const handleKeyDown = (e) => {
      // Ignorar se estiver digitando em um input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Números e caracteres do teclado
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        setNumber((prev) => prev + e.key);
        playDTMFTone(e.key);
      } else if (e.key === '*') {
        e.preventDefault();
        setNumber((prev) => prev + '*');
        playDTMFTone('*');
      } else if (e.key === '#') {
        e.preventDefault();
        setNumber((prev) => prev + '#');
        playDTMFTone('#');
      } else if (e.key === 'Enter' && number.trim()) {
        e.preventDefault();
        handleCall();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setNumber((prev) => prev.slice(0, -1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, isMinimized, number, handleCall]);

  // Forçar pointerEvents: none em todos os elementos do Dialog quando minimizado
  useEffect(() => {
    if (isMinimized && dialogRef.current) {
      // Encontrar o container do Dialog
      const dialogContainer = dialogRef.current.closest('.MuiDialog-container');
      const dialogRoot = dialogRef.current.closest('.MuiDialog-root');
      
      if (dialogContainer) {
        dialogContainer.style.pointerEvents = 'none';
        dialogContainer.style.position = 'static';
      }
      
      if (dialogRoot) {
        dialogRoot.style.pointerEvents = 'none';
      }
      
      // Garantir que o Paper (círculo) seja clicável
      if (dialogRef.current) {
        dialogRef.current.style.pointerEvents = 'auto';
      }
    }
  }, [isMinimized]);

  const dialPadNumbers = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["*", "0", "#"],
  ];

  // Quando minimizado, renderizar apenas o círculo flutuante sem Dialog
  if (isMinimized && open) {
    // Se não há posição definida, usar posição padrão (canto inferior direito)
    const defaultX = window.innerWidth - 80; // 60px (largura) + 20px (margem)
    const defaultY = window.innerHeight - 80; // 60px (altura) + 20px (margem)
    
    return (
      <Box
        ref={dialogRef}
        className={`${classes.minimizedContent} drag-handle minimizedContent`}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleMouseDown(e);
        }}
        style={{
          position: 'fixed',
          left: position.x > 0 ? position.x : defaultX,
          top: position.y > 0 ? position.y : defaultY,
          width: 60,
          height: 60,
          borderRadius: '50%',
          backgroundColor: '#1976d2',
          boxShadow: '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          zIndex: 1300,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          transition: isDragging ? 'none' : 'all 0.2s ease-in-out',
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = '#1565c0';
            e.currentTarget.style.transform = 'scale(1.1)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = '#1976d2';
            e.currentTarget.style.transform = 'scale(1)';
          }
        }}
      >
        <span style={{ fontSize: '2rem', lineHeight: 1, pointerEvents: 'none' }}>
          📞
        </span>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Ao clicar no backdrop (fora do diálogo), minimizar ao invés de fechar
        if (reason === 'backdropClick') {
          setIsMinimized(true);
          return;
        }
        handleClose();
      }}
      className={classes.dialog}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        ref: dialogRef,
        style: position.x || position.y ? {
          position: 'fixed',
          top: position.y || 'auto',
          left: position.x || 'auto',
          margin: 0,
        } : {},
      }}
    >
      {!isMinimized && (
        <DialogTitle
          className="drag-handle"
          onMouseDown={handleMouseDown}
          style={{ cursor: 'move', userSelect: 'none' }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Telefone VoIP</Typography>
            <Box>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMinimized(true);
                }}
                size="small"
              >
                <MinimizeIcon />
              </IconButton>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
      )}
      {!isMinimized && (
        <DialogContent>
        {/* Status da conexão */}
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2">
              Status: {isConnected ? (
                <span style={{ color: "#4caf50", fontWeight: "bold" }}>Conectado</span>
              ) : (
                <span style={{ color: "#f44336", fontWeight: "bold" }}>Desconectado</span>
              )}
            </Typography>
            {asteriskUrl && (
              <Typography variant="caption" style={{ display: "block", marginTop: 4, color: "#666" }}>
                URL: {asteriskUrl}
              </Typography>
            )}
            {isConnected && (
              <Typography variant="caption" style={{ display: "block", marginTop: 4, color: isAuthenticated ? "#4caf50" : "#ff9800", fontSize: "0.7rem" }}>
                {isAuthenticated ? "✓ Autenticado" : "⏳ Aguardando autenticação..."}
              </Typography>
            )}
            {lastMessage && (
              <Typography variant="caption" style={{ display: "block", marginTop: 4, color: "#666", fontSize: "0.7rem" }}>
                {lastMessage}
              </Typography>
            )}
            {connectionError && !isConnected && (
              <Typography variant="caption" style={{ display: "block", marginTop: 4, color: "#f44336", fontSize: "0.7rem" }}>
                {connectionError}
              </Typography>
            )}
          </Box>
          {!isConnected && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={connectToAsterisk}
              disabled={!asteriskUrl || !extension || !password}
            >
              Conectar
            </Button>
          )}
          {isConnected && !isAuthenticated && (
            <Typography variant="caption" style={{ color: "#ff9800", fontSize: "0.7rem" }}>
              Autenticando...
            </Typography>
          )}
          {isConnected && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => {
                if (sipUA) {
                  sipUA.stop();
                  setSipUA(null);
                }
                if (currentCall) {
                  hangupCall(currentCall);
                  setCurrentCall(null);
                }
                setIsConnected(false);
                setIsAuthenticated(false);
              }}
            >
              Desconectar
            </Button>
          )}
        </Box>

        {/* Configurações de ramal e senha */}
        <Box mb={2}>
          <IconButton
            size="small"
            onClick={() => setShowSettings(!showSettings)}
            color={showSettings ? "primary" : "default"}
          >
            <SettingsIcon />
          </IconButton>
          <Typography variant="caption" display="inline" style={{ marginLeft: 8 }}>
            Configurações
          </Typography>
        </Box>

        {showSettings && (
          <Paper className={classes.settingsSection}>
            <TextField
              fullWidth
              label="Ramal"
              value={extension}
              onChange={(e) => setExtension(e.target.value)}
              margin="dense"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Senha"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="dense"
              variant="outlined"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={useInsecureConnection}
                  onChange={(e) => {
                    setUseInsecureConnection(e.target.checked);
                    localStorage.setItem("voip_use_insecure", e.target.checked ? "true" : "false");
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  Usar conexão insegura (ws://)
                  <br />
                  <Typography variant="caption" style={{ color: "#f44336" }}>
                    Ignora certificado SSL inválido - menos seguro
                  </Typography>
                </Typography>
              }
              style={{ marginTop: 8, marginBottom: 8 }}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveCredentials}
              style={{ marginTop: 8 }}
            >
              Salvar Credenciais
            </Button>
          </Paper>
        )}

        {/* Display do número */}
        <Paper className={classes.numberDisplay}>
          {number || "Digite o número"}
        </Paper>

        {/* Teclado telefônico */}
        <Grid container spacing={1} className={classes.dialpad}>
          {dialPadNumbers.map((row, rowIndex) => (
            <Grid container item xs={12} key={rowIndex} spacing={1}>
              {row.map((digit) => (
                <Grid item xs={4} key={digit}>
                  <Button
                    fullWidth
                    variant="outlined"
                    className={classes.dialButton}
                    onClick={() => handleDial(digit)}
                    disabled={callStatus === "connected" || callStatus === "calling"}
                  >
                    {digit}
                  </Button>
                </Grid>
              ))}
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleBackspace}
              disabled={!number || callStatus === "connected" || callStatus === "calling" || callStatus === "ringing"}
            >
              ← Apagar
            </Button>
          </Grid>
        </Grid>

        {/* Botões de ação */}
        <Box mt={2} display="flex" justifyContent="space-around" flexWrap="wrap">
          {callStatus === "idle" && (
            <Tooltip title="Discar">
              <IconButton
                className={classes.callButton}
                onClick={handleCall}
                disabled={!number || !isConnected}
              >
                <CallIcon />
              </IconButton>
            </Tooltip>
          )}

          {callStatus === "ringing" && (
            <>
              <Tooltip title="Atender">
                <IconButton
                  className={classes.callButton}
                  onClick={handleAnswerCall}
                >
                  <CallIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rejeitar">
                <IconButton
                  className={classes.endCallButton}
                  onClick={handleRejectCall}
                >
                  <CallEndIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          {(callStatus === "connected" || callStatus === "calling" || callStatus === "onHold") && (
            <>
              {callStatus === "connected" && (
                <>
                  <Tooltip title="Colocar em espera">
                    <IconButton
                      className={classes.actionButton}
                      onClick={handleHold}
                    >
                      <PauseIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Transferir">
                    <IconButton
                      className={classes.actionButton}
                      onClick={handleTransfer}
                    >
                      <SwapCallsIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              {callStatus === "onHold" && (
                <>
                  <Tooltip title="Retomar chamada">
                    <IconButton
                      className={classes.actionButton}
                      onClick={handleUnhold}
                    >
                      <CallIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Transferir">
                    <IconButton
                      className={classes.actionButton}
                      onClick={handleTransfer}
                    >
                      <SwapCallsIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              <Tooltip title="Encerrar chamada">
                <IconButton
                  className={classes.endCallButton}
                  onClick={handleEndCall}
                >
                  <CallEndIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </DialogContent>
      )}
    </Dialog>
  );
};

export default PhoneDialer;


